import { FC } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Degree, useGetCoursePlanQuery } from '@teachur/http-client';
import {
  ProgressSegment,
  SegmentedProgressBar,
} from '@new/common/ui/SegmentedProgressBar/SegmentedProgressBar';
import { useUnifiedCredits } from '@new/features/degree/hooks/useUnifiedCredits';
import { getPlanCreditSegments } from '../../lib/courseSnapshot';
import { NoPlanCreditsSummary } from './NoPlanCreditsSummary';

interface DegreeCreditsBreakdownProps {
  degree: Pick<Degree, 'id' | 'type'>;
}

const TRANSFERRED_COLOR = 'bg-(--secondary-B900)';
const NEWLANE_COLOR = 'bg-(--primary-O900)';
const REMAINING_COLOR = 'bg-(--neutral-black-NB200)';

const LEGEND = [
  { label: 'Transferred credits', color: TRANSFERRED_COLOR },
  { label: 'Taken on Newlane', color: NEWLANE_COLOR },
  { label: 'Remaining credits', color: REMAINING_COLOR },
];

const COURSE_BUILDER_ROUTE = '/course-builder';

const SeeMyPlanLink: FC = () => (
  <Link
    to={COURSE_BUILDER_ROUTE}
    className="inline-flex items-center gap-1.5 [font-size:var(--fs-l5)]! bold text-(--primary-O900)! hover:underline"
  >
    See my plan
    <ArrowRight aria-hidden className="size-4" />
  </Link>
);

export const DegreeCreditsBreakdown: FC<DegreeCreditsBreakdownProps> = ({ degree }) => {
  const { data: coursePlan, isLoading } = useGetCoursePlanQuery();
  const credits = useUnifiedCredits(degree.type);

  if (isLoading) {
    return null;
  }

  const plan = coursePlan?.plans.find((savedPlan) => savedPlan.degree.id === degree.id);

  if (!plan) {
    return (
      <NoPlanCreditsSummary
        transferredCredits={credits.transfer.total}
        newlaneCredits={credits.earned.total}
      />
    );
  }

  const segments = getPlanCreditSegments(plan);
  const required = segments.transferred + segments.newlane + segments.remaining;
  const earnedTotal = segments.transferred + segments.newlane;

  const bars: ProgressSegment[] = [
    {
      id: 'transferred',
      groupId: null,
      credits: segments.transferred,
      color: TRANSFERRED_COLOR,
      title: `${segments.transferred} transferred credits`,
    },
    {
      id: 'newlane',
      groupId: null,
      credits: segments.newlane,
      color: NEWLANE_COLOR,
      title: `${segments.newlane} credits taken on Newlane`,
    },
    {
      id: 'remaining',
      groupId: null,
      credits: segments.remaining,
      color: REMAINING_COLOR,
      title: `${segments.remaining} credits remaining`,
    },
  ].filter((bar) => bar.credits > 0);

  return (
    <div className="flex flex-col gap-3 border-b border-(--neutral-black-NB200) bg-(--primary-O100) px-5 py-5 sm:px-7">
      <div className="flex items-center justify-between text-(--neutral-black-NB700)">
        <span className="l5 medium">Degree progress</span>
        <span className="flex items-baseline gap-1">
          <span className="p3 semibold text-(--neutral-black-NB900)">{earnedTotal}</span>
          <span className="l5 text-(--neutral-black-NB500)">/{required} credits</span>
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={required}
        aria-valuenow={earnedTotal}
      >
        <SegmentedProgressBar
          segments={bars}
          total={required}
          legend={LEGEND}
          legendExtra={<SeeMyPlanLink />}
        />
      </div>
    </div>
  );
};
